import { action } from '@ember/object';
import Route from '@ember/routing/route';
import type PublicTransition from '@ember/routing/transition';
import { service } from '@ember/service';

import { hashSettled } from 'rsvp';

import type { RequestError, Service } from 'webapp-types';
import { ModelName } from 'webapp/enums/enums';

import type ReportsEditorReportController from 'webapp/controllers/reports/editor/report';

import { DEFAULT_REPORT_TITLE } from 'webapp/models-custom/report';
import type ReportRecord from 'webapp/models/report';

export default class ReportsEditorReportRoute extends Route {
	@service('store') declare storeService: Service.Store;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('model-manager/templates') declare mmTemplatesService: Service.ModelManager.Templates;
	@service('model-manager/reports') declare mmReportsService: Service.ModelManager.Reports;
	@service('model-manager/reminders') declare mmRemindersService: Service.ModelManager.Reminders;
	@service('model-manager/report-schedule-settings')
	declare mmReportScheduleSettingsService: Service.ModelManager.ReportScheduleSettings;
	@service('account') declare accountService: Service.Account;
	@service('request') declare requestService: Service.Request;
	@service('model-manager/metrics') declare mmMetricsService: Service.ModelManager.Metrics;

	enteringTransition: PublicTransition | null = null;

	override beforeModel(transition: PublicTransition): Promise<unknown> {
		const promises: Record<string, Promise<unknown>> = {};

		promises.initial = this.navigationService.initialRequestsPromise;
		promises.reports = this.mmReportsService.requestReports();
		promises.accountTemplates = this.mmTemplatesService.requestReportAccountTemplates(); // bcos they also can be editable

		// non-blocking
		void this.mmRemindersService.requestReminders();
		void this.mmReportScheduleSettingsService.requestReportSchedules();
		void this.mmMetricsService.prepareForMetricEditing();

		this.enteringTransition = transition;

		return hashSettled(promises);
	}

	override model(params: {
		report_id: string;
	}): Promise<ReportRecord | null> | ReportRecord | null {
		if (!params) {
			return null;
		}

		if (params.report_id === 'null') {
			return this.createNewReport().then(() => null);
		}

		const model = this.storeService.peekRecord(
			ModelName.REPORT,
			params.report_id,
		) as ReportRecord;
		return this.validateReportAccess(model);
	}

	private async createNewReport(): Promise<void> {
		const report = this.mmReportsService.createNewRecord({
			title: DEFAULT_REPORT_TITLE,
		});
		await report
			.save()
			.then((reportRecord) => {
				this.navigationService.replaceRoute('reports.editor.report', reportRecord);
			})
			.catch((errorData: RequestError) => {
				this.requestService.handleGenericThrownError(errorData);
				this.infolayerService.error('Failed to create new report.');
				this.navigationService.replaceRoute('reports');
			});
	}

	private validateReportAccess(model: ReportRecord | null): ReportRecord | null {
		if (!model || this.accountService.isDemoAccount) {
			this.navigationService.replaceRoute('reports');
			this.infolayerService.error(
				'Report does not exist or you do not have permissions to customize it.',
			);
			return null;
		}

		const reportModel = this.mmReportsService.findModel(model.id!);
		if (!reportModel?.canCurrentUserEdit) {
			this.infolayerService.error('You are not allowed to customize this report.');
			this.navigationService.replaceRoute('reports');
			return null;
		}

		return model;
	}

	override afterModel(model: ReportRecord): Promise<unknown> {
		if (model.id && !this.mmReportsService.isReportDataRequested(model.id)) {
			return this.mmReportsService.requestReportData(model.id);
		}

		return Promise.resolve();
	}

	override setupController(
		controller: ReportsEditorReportController,
		model: ReportRecord,
		transition: PublicTransition,
	): void {
		super.setupController(controller, model, transition);

		controller.enteringTransition = this.enteringTransition;

		const report = this.mmReportsService.createModelFromRecord(model);
		controller.report = report;
		controller.selectedSlideId = report.slides[0]?.id || null;
		controller.isPagelessView = report?.isPageless ?? true;
	}

	override resetController(controller: ReportsEditorReportController, isExiting: boolean): void {
		if (isExiting) {
			controller.selectedSlideId = null;
			controller.enteringTransition = null;
		}
	}

	@action
	override willTransition() {
		const currentReportRecord = this.modelFor('reports.editor.report') as ReportRecord;
		const currentModel = this.mmReportsService.findModel(currentReportRecord.id!);

		if (currentModel?.isEmpty && currentModel.title === DEFAULT_REPORT_TITLE) {
			void currentModel.cleanup();
		}
	}
}
